/**
 * <cart-line-actions> web component.
 *
 * Single Remove action with auto-save-to-wishlist:
 *   1. Click Remove → the cart line is removed immediately (server-side) and
 *      the cart section re-renders, so totals / subtotal / item count all
 *      update right away.
 *   2. A pinned "Saved to wishlist" undo toast appears with a 5s window
 *      (timer paused on hover/focus).
 *   3. On timeout: the line stays removed + a best-effort WishlistState.add.
 *   4. On Undo within the window: the line is re-added to the cart.
 *
 * The removal lifecycle is owned by the module-scoped CartRemovalManager
 * singleton — section morphs destroy <cart-line-actions> element instances,
 * so the timer / undo state cannot live on the element itself.
 */

import WishlistState from "./wishlist-state.js";
import { morphSection } from "@theme/section-renderer";

const UNDO_WINDOW_MS = 5000;

/* ── Shared helpers ─────────────────────────────────────────────────────── */

/** Collect every cart section id currently on the page. */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll("cart-items-component").forEach((el) => {
    const id = el instanceof HTMLElement ? el.dataset.sectionId : null;
    if (id) ids.add(id);
  });
  return [...ids];
}

/** Morph every returned section into the live DOM. */
function applySections(sections) {
  if (!sections) return;
  for (const [id, html] of Object.entries(sections)) {
    if (typeof html !== "string") continue;
    try {
      morphSection(id, html);
    } catch (err) {
      console.warn("[cart-line-actions] morphSection failed:", err);
    }
  }
}

/* ── Removal manager (module-scope, survives section morphs) ─────────────── */

class CartRemovalManagerClass {
  /** lineKey → { meta, snapshot, toast, timerId, startedAt, remaining } */
  _pending = new Map();
  _stack = null;

  constructor() {
    // The cart drawer rebuilds its <dialog> contents on every section morph
    // (cart-drawer.js #inflateTemplate), which detaches the toast stack — and
    // one cart change fires several morphs back-to-back, so without this the
    // toast would vanish almost immediately. Re-attach it after each morph.
    document.addEventListener("section:morph", () => {
      if (this._pending.size === 0) return;
      // Defer past the synchronous #inflateTemplate rebuild for this morph.
      requestAnimationFrame(() => {
        if (this._pending.size > 0) this.#mountStack();
      });
    });
  }

  /** The pinned toast container, created lazily. */
  get stack() {
    if (!this._stack) {
      this._stack = document.createElement("div");
      this._stack.className = "cart-undo-toast-stack";
    }
    return this._stack;
  }

  /**
   * Attach the toast stack to the correct context and return it.
   *
   * The cart drawer is a modal <dialog>, which makes everything OUTSIDE it
   * `inert` — a toast on <body> would render above the drawer but its Undo
   * button could not be clicked. So the stack is parented INSIDE the open
   * drawer dialog (as a sibling of the morphing section, so a cart re-render
   * leaves it intact); on the standalone cart page it lives on <body>.
   */
  #mountStack() {
    const stack = this.stack;
    const drawer = document.querySelector("dialog.cart-drawer__dialog[open]");
    // appendChild also re-orders an existing child to last — keeping the
    // toast on top — so it's safe to call on every morph.
    (drawer || document.body).appendChild(stack);
    return stack;
  }

  /**
   * Remove a cart line immediately and show the undo toast.
   * @param {object} meta - line metadata (key, handle, title, …).
   * @param {*} itemsComponent - the owning <cart-items-component>, if any.
   * @returns {Promise<boolean>} true if the removal committed.
   */
  async remove(meta, itemsComponent) {
    if (!meta.key || this._pending.has(meta.key)) return false;

    // Snapshot the line BEFORE removing it so Undo can faithfully re-add it
    // (variant, quantity, line-item properties, selling plan). Best-effort:
    // if it fails, removal still proceeds but Undo is disabled.
    let snapshot = null;
    try {
      snapshot = await this.#snapshotLine(meta.key);
    } catch (err) {
      console.warn("[cart-line-actions] line snapshot failed; Undo disabled:", err);
    }

    try {
      // Route through the component's serialized mutation queue so a removal
      // can't race a concurrent quantity change. Fall back to a direct change
      // request only if there's no owning component.
      if (typeof itemsComponent?.updateQuantity === "function") {
        // updateQuantity resolves to undefined on BOTH success and failure —
        // it swallows cart errors internally — so the return value tells us
        // nothing. Verify the outcome from the morphed DOM instead: if a line
        // with this key survives the re-render, the change was rejected.
        await itemsComponent.updateQuantity({
          key: meta.key,
          quantity: 0,
          action: "clear",
        });
        const survivor = document.querySelector(
          `cart-line-actions[data-line-key="${CSS.escape(meta.key)}"]`
        );
        if (survivor) throw new Error("cart change rejected");
      } else {
        await this.#changeCartLine(meta.key, 0);
      }
    } catch (err) {
      console.error("[cart-line-actions] removal failed:", err);
      return false;
    }

    const entry = { meta, snapshot };
    entry.toast = this.#buildToast(meta);
    this.#mountStack().appendChild(entry.toast);
    this._pending.set(meta.key, entry);
    this.#startTimer(meta.key);
    return true;
  }

  async #snapshotLine(key) {
    const res = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`/cart.js returned ${res.status}`);
    const cart = await res.json();
    const item = (cart.items || []).find((i) => i.key === key);
    if (!item) throw new Error("line not found in cart");
    return {
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {},
      selling_plan: item.selling_plan_allocation?.selling_plan?.id || null,
    };
  }

  async #changeCartLine(key, quantity) {
    const sections = cartSectionIds();
    const body = { id: key, quantity };
    if (sections.length) body.sections = sections.join(",");

    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`cart change returned ${res.status}`);
    const parsed = await res.json();

    applySections(parsed.sections);
    document.dispatchEvent(new CustomEvent("cart:update", { detail: { data: parsed } }));
    return parsed;
  }

  async #addCartLine(snapshot) {
    const sections = cartSectionIds();
    const body = {
      id: snapshot.id,
      quantity: snapshot.quantity,
      properties: snapshot.properties,
    };
    if (snapshot.selling_plan) body.selling_plan = snapshot.selling_plan;
    if (sections.length) body.sections = sections.join(",");

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`cart add returned ${res.status}`);
    const parsed = await res.json();

    applySections(parsed.sections);
    document.dispatchEvent(new CustomEvent("cart:update", { detail: { data: parsed } }));
    return parsed;
  }

  /* ── Undo toast ───────────────────────────────────────────────────────── */

  #buildToast(meta) {
    const toast = document.createElement("div");
    toast.className = "cart-undo-toast";
    toast.setAttribute("role", "status");

    if (meta.image) {
      const img = document.createElement("img");
      img.className = "cart-undo-toast__thumb";
      img.src = meta.image;
      img.alt = "";
      img.width = 40;
      img.height = 40;
      toast.appendChild(img);
    }

    const body = document.createElement("span");
    body.className = "cart-undo-toast__body";
    const title = document.createElement("span");
    title.className = "cart-undo-toast__title";
    title.textContent = meta.savedLabel || "Saved to wishlist";
    const sub = document.createElement("span");
    sub.className = "cart-undo-toast__sub";
    sub.textContent = meta.title || "";
    body.append(title, sub);
    toast.appendChild(body);

    const undoBtn = document.createElement("button");
    undoBtn.type = "button";
    undoBtn.className = "cart-undo-toast__undo";
    undoBtn.textContent = meta.undoLabel || "Undo";
    undoBtn.addEventListener("click", () => this.undo(meta.key));
    toast.appendChild(undoBtn);

    // Pause the countdown while the user is interacting with the toast.
    toast.addEventListener("mouseenter", () => this.#pauseTimer(meta.key));
    toast.addEventListener("mouseleave", () => this.#resumeTimer(meta.key));
    toast.addEventListener("focusin", () => this.#pauseTimer(meta.key));
    toast.addEventListener("focusout", () => this.#resumeTimer(meta.key));

    undoBtn.focus();
    return toast;
  }

  #dismissToast(entry) {
    const { toast } = entry;
    if (!toast) return;
    toast.classList.add("cart-undo-toast--leaving");
    toast.addEventListener("animationend", () => toast.remove(), { once: true });
    // Fallback in case the animation event never fires.
    setTimeout(() => toast.remove(), 300);
  }

  /* ── Timer ────────────────────────────────────────────────────────────── */

  #startTimer(key) {
    const entry = this._pending.get(key);
    if (!entry) return;
    entry.remaining = UNDO_WINDOW_MS;
    entry.startedAt = Date.now();
    entry.timerId = setTimeout(() => this.#commit(key), entry.remaining);
  }

  #pauseTimer(key) {
    const entry = this._pending.get(key);
    if (!entry || !entry.timerId) return;
    clearTimeout(entry.timerId);
    entry.timerId = null;
    entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
  }

  #resumeTimer(key) {
    const entry = this._pending.get(key);
    if (!entry || entry.timerId) return;
    entry.startedAt = Date.now();
    entry.timerId = setTimeout(() => this.#commit(key), entry.remaining);
  }

  /* ── Resolution ───────────────────────────────────────────────────────── */

  /** Undo window elapsed: the line stays removed, add it to the wishlist. */
  async #commit(key) {
    const entry = this._pending.get(key);
    if (!entry) return;
    this._pending.delete(key);
    if (entry.timerId) clearTimeout(entry.timerId);
    this.#dismissToast(entry);

    const handle = entry.meta.handle;
    if (handle && !WishlistState.isLiked(handle)) {
      try {
        await WishlistState.add(handle, entry.meta.productId);
      } catch (err) {
        console.warn("[cart-line-actions] wishlist add failed:", err);
      }
    }
  }

  /** User tapped Undo: re-add the removed line to the cart. */
  async undo(key) {
    const entry = this._pending.get(key);
    if (!entry) return;
    this._pending.delete(key);
    if (entry.timerId) clearTimeout(entry.timerId);
    this.#dismissToast(entry);

    if (!entry.snapshot) return; // snapshot failed — nothing to restore
    try {
      await this.#addCartLine(entry.snapshot);
    } catch (err) {
      console.error("[cart-line-actions] undo re-add failed:", err);
    }
  }
}

/** Singleton — survives section morphs that destroy element instances. */
const CartRemovalManager = new CartRemovalManagerClass();

/* ── Element ────────────────────────────────────────────────────────────── */

class CartLineActions extends HTMLElement {
  connectedCallback() {
    if (this._delegated) return;
    this._delegated = true;

    // Delegated so it survives section-morph re-renders that replace the
    // inner Remove button while keeping this element instance intact.
    this.addEventListener("click", (e) => {
      // Safari has issues with closest() traversal from SVG elements into the
      // HTML DOM. Instead, check if the remove button contains the click target.
      const removeBtn = this.querySelector('[data-action="remove"]');
      if (removeBtn?.contains(e.target)) this.#handleRemoveClick(e);
    });
  }

  get #meta() {
    return {
      key: this.dataset.lineKey || "",
      handle: this.dataset.productHandle || "",
      productId: this.dataset.productId || null,
      title: this.dataset.productTitle || "",
      image: this.dataset.productImage || "",
      savedLabel: this.dataset.savedLabel || "",
      undoLabel: this.dataset.undoLabel || "",
    };
  }

  async #handleRemoveClick(event) {
    event.preventDefault();
    if (this._removing) return;
    this._removing = true;

    const meta = this.#meta;
    document.dispatchEvent(
      new CustomEvent("cart:item-removed", {
        detail: {
          product_handle: meta.handle,
          product_id: meta.productId,
          line_index: parseInt(this.dataset.lineIndex || "0", 10),
        },
      })
    );

    // Optimistic fade while the removal request is in flight; the section
    // morph then drops the row entirely.
    const tableRow = this.closest(".cart-items__table-row");
    if (tableRow) tableRow.dataset.pendingRemove = "true";

    const itemsComponent = this.closest("cart-items-component");
    const ok = await CartRemovalManager.remove(meta, itemsComponent);
    if (!ok) {
      // Removal failed — restore the row so the user can retry.
      if (tableRow) delete tableRow.dataset.pendingRemove;
      this._removing = false;
    }
  }
}

if (!customElements.get("cart-line-actions")) {
  customElements.define("cart-line-actions", CartLineActions);
}
